import * as React from 'react';
import * as Dialog from '@naan/primitives/modals';
import { HelpCircleIcon } from '@naan/icons/help-circle-icon';
import { IconButton } from '@naan/primitives/button';
import { observer } from 'mobx-react-lite';
import { Story } from '@core/models/story-manager';
import { styled } from '@naan/stitches.config';
import { usePlayerModel } from 'player/views/player-model-context';
import { useReaction } from '@common/hooks/use-reaction';
import { AppFactory } from 'app/app-factory';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftSmallIcon } from '@naan/icons/arrow-left-icon';
import { ReturnNavState } from 'components/nav/return-nav-state';
import { homePath } from 'components/nav/path-helpers';
import { HStack } from '@naan/primitives/stack';
import { HSpacer } from '@naan/primitives/spacer';

import __ from '@core/lib/localization';

export const Wrapper = styled('div', {
  $$backgroundColor: '$colors$white',

  position: 'absolute',
  top: '0',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '56px', // TODO: put in a variable
  padding: '0 8px',
  flexShrink: '0',
  background: '$$backgroundColor',
  zIndex: '2',
  transition: 'transform .3s, background .3s',
  color: '$white',

  '& > .left': {
    position: 'absolute',
    left: 0,
  },
  '& > .right': {
    position: 'absolute',
    right: '8px',
  },

  '& > .date': {
    display: 'none',
    '@playerSmallAndUp': {
      display: 'block',
    },
  },

  '&.playing': {
    transform: 'translateY(-100%)',
    // $$backgroundColor: paletteKey('$$script__backgroundColor_playing'),
  },
});

export const Logo = () => {
  return (
    <svg
      width={163}
      height={40}
      viewBox="0 0 163 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        opacity="0.502232"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.09387 29.3201C9.09387 29.614 8.85452 29.8545 8.56192 29.8545H0.53195C0.239355 29.8545 0 29.614 0 29.3201V28.5402C0 28.2463 0.239355 28.0059 0.53195 28.0059H8.56192C8.85452 28.0059 9.09387 28.2463 9.09387 28.5402V29.3201Z"
        fill="white"
      />
      <path
        opacity="0.502232"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.5121 29.3203C18.5121 29.6142 18.2728 29.8547 17.9802 29.8547H10.8308C10.5382 29.8547 10.2988 29.6142 10.2988 29.3203V28.5404C10.2988 28.2465 10.5382 28.006 10.8308 28.006H17.9802C18.2728 28.006 18.5121 28.2465 18.5121 28.5404V29.3203Z"
        fill="white"
      />
      <path
        opacity="0.502232"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M35.7411 29.3201C35.7411 29.614 35.5018 29.8545 35.2092 29.8545H28.5339C28.2413 29.8545 28.002 29.614 28.002 29.3201V28.5402C28.002 28.2463 28.2413 28.0059 28.5339 28.0059H35.2092C35.5018 28.0059 35.7411 28.2463 35.7411 28.5402V29.3201Z"
        fill="white"
      />
      <path
        opacity="0.502232"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M26.7946 29.3203C26.7946 29.6142 26.5552 29.8547 26.2626 29.8547H23.612C23.3194 29.8547 23.0801 29.6142 23.0801 29.3203V28.5404C23.0801 28.2465 23.3194 28.006 23.612 28.006H26.2626C26.5552 28.006 26.7946 28.2465 26.7946 28.5404V29.3203Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.00390625 26.1259V24.2656H1.41231V8.86031H0.00390625V7H5.29892V8.86031H3.7861V24.2656H6.68122V20.886H9.05486V26.1259H0.00390625Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.2594 26.1267V25.3144C14.7376 25.891 13.9812 26.3102 12.7816 26.3102C11.2688 26.3102 10.6687 25.5765 10.6687 24.0046V15.9349H9.57324V14.2842H12.7554V23.6114C12.7554 24.1618 12.9381 24.5023 13.7465 24.5023C14.7901 24.5023 15.2594 24.0046 15.2594 23.0352V15.9349H14.3727V14.2842H17.346V24.476H18.4415V26.1267H15.2594Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M24.6515 17.0601C24.6515 16.3789 24.4694 15.9075 23.4259 15.9075C22.5649 15.9075 21.9912 16.2741 21.9912 17.3746V23.0338C21.9912 23.9508 22.4084 24.5009 23.4259 24.5009C24.3125 24.5009 24.6515 24.1604 24.6515 23.3481V17.0601ZM24.2368 26.3089C23.1937 26.3089 22.62 26.0993 21.9938 25.418V32.3498H23.0892V34.0002H18.8115V32.3498H19.9071V15.9336H18.8115V14.2829H21.9938V15.0953C22.5418 14.4928 23.3764 14.0997 24.3676 14.0997C26.0369 14.0997 26.7408 14.9642 26.7408 16.5888V23.4267C26.7408 25.261 25.9844 26.3089 24.2368 26.3089Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M32.5625 21.7787C32.5625 20.9404 31.9626 20.5997 31.1021 20.5997C30.5803 20.5997 30.1631 20.7568 30.1631 21.5427V23.6126C30.1631 24.2939 30.424 24.556 31.1021 24.556C32.0151 24.556 32.5625 24.2153 32.5625 23.2459V21.7787ZM32.5614 26.1253V25.3129C31.9357 25.9942 31.101 26.3087 30.1099 26.3087C28.9361 26.3087 28.1016 25.7846 28.1016 23.9245V21.5926C28.1016 19.8373 28.8055 18.894 30.5529 18.894C31.3876 18.894 32.0396 19.1039 32.5614 19.523V17.0079C32.5614 16.2742 32.3268 15.8289 31.4139 15.8289C30.7619 15.8289 30.292 16.0121 30.292 17.0079V17.7153H28.2579V16.903C28.2579 15.0427 29.2228 14.0997 31.4658 14.0997C33.6308 14.0997 34.6218 14.8855 34.6218 16.903V24.4746H35.7173V26.1253H32.5614Z"
        fill="white"
      />
      <path
        d="M45.661 14.1H41.207V26H45.661C46.562 26 47.395 25.864 48.177 25.575C48.942 25.286 49.588 24.878 50.132 24.351C50.676 23.824 51.084 23.195 51.373 22.447C51.662 21.716 51.815 20.917 51.815 20.05C51.815 19.2 51.662 18.401 51.373 17.653C51.084 16.922 50.676 16.293 50.132 15.766C49.588 15.239 48.942 14.831 48.177 14.542C47.395 14.253 46.562 14.1 45.661 14.1ZM45.661 16.276C46.834 16.276 47.752 16.616 48.398 17.279C49.044 17.942 49.384 18.86 49.384 20.05C49.384 21.24 49.044 22.175 48.398 22.838C47.752 23.501 46.834 23.824 45.661 23.824H43.536V16.276H45.661ZM56.8368 17.194C55.8338 17.194 55.0178 17.483 54.4058 18.044C53.7938 18.622 53.4538 19.336 53.4028 20.22H55.5448C55.5448 19.88 55.6638 19.608 55.9188 19.404C56.1568 19.2 56.4628 19.081 56.8368 19.081C57.2108 19.081 57.5168 19.2 57.7208 19.421C57.9248 19.659 58.0438 19.982 58.0438 20.39V20.713L55.8168 21.121C54.8648 21.291 54.1678 21.58 53.7598 22.022C53.3518 22.464 53.1478 23.025 53.1478 23.705C53.1478 24.47 53.3858 25.082 53.8788 25.524C54.3548 25.966 54.9838 26.187 55.7658 26.187C56.3268 26.187 56.8198 26.085 57.2278 25.847C57.6358 25.626 57.9758 25.32 58.2308 24.946L58.3668 26H60.2538V20.339C60.2538 19.319 59.9308 18.554 59.3188 18.01C58.7068 17.466 57.8738 17.194 56.8368 17.194ZM58.0438 22.192V22.447C58.0438 22.651 58.0098 22.855 57.9588 23.059C57.8908 23.28 57.8228 23.45 57.7378 23.586C57.5848 23.841 57.3808 24.028 57.1428 24.164C56.8878 24.3 56.6328 24.368 56.3608 24.368C56.0378 24.368 55.7828 24.3 55.6128 24.147C55.4428 23.994 55.3578 23.79 55.3578 23.518C55.3578 23.025 55.6808 22.702 56.3268 22.549L58.0438 22.192ZM64.6334 17.381H62.4234V26H64.6334V17.381ZM63.5284 13.539C63.1544 13.539 62.8314 13.675 62.5764 13.93C62.3044 14.185 62.1854 14.491 62.1854 14.848C62.1854 15.222 62.3044 15.528 62.5764 15.783C62.8314 16.038 63.1544 16.157 63.5284 16.157C63.8854 16.157 64.1914 16.038 64.4634 15.783C64.7354 15.528 64.8714 15.222 64.8714 14.848C64.8714 14.491 64.7354 14.185 64.4634 13.93C64.1914 13.675 63.8854 13.539 63.5284 13.539ZM69.2154 13.59H67.0054V26H69.2154V13.59ZM79.0335 17.381H76.6365L74.9535 22.668L74.7835 23.416L74.6135 22.668L72.9305 17.381H70.5505L73.6105 25.898L72.3015 29.655H74.5965L79.0335 17.381ZM87.2572 13.879C86.6792 13.879 86.1692 13.981 85.6932 14.151C85.2172 14.321 84.8262 14.576 84.5032 14.882C84.1632 15.205 83.9252 15.579 83.7552 16.021C83.5852 16.463 83.5002 16.922 83.5002 17.432C83.5002 18.044 83.6022 18.554 83.8402 18.996C84.0782 19.438 84.4352 19.829 84.9112 20.169C85.3872 20.509 86.0672 20.798 86.9172 21.07C87.6652 21.325 88.1922 21.58 88.5322 21.852C88.8552 22.124 89.0252 22.447 89.0252 22.821C89.0252 23.229 88.8722 23.552 88.6002 23.79C88.3112 24.045 87.9372 24.164 87.4612 24.164C86.8832 24.164 86.4242 24.011 86.1012 23.705C85.7782 23.399 85.6082 22.94 85.5912 22.362H83.2282C83.2282 22.94 83.3302 23.45 83.5342 23.926C83.7212 24.402 83.9932 24.81 84.3502 25.15C84.6902 25.49 85.1322 25.762 85.6592 25.949C86.1692 26.136 86.7642 26.221 87.4272 26.221C87.9712 26.221 88.4982 26.153 88.9912 25.983C89.4672 25.813 89.8922 25.575 90.2492 25.269C90.6062 24.963 90.8782 24.589 91.0822 24.13C91.2862 23.688 91.3882 23.178 91.3882 22.617C91.3882 21.937 91.2522 21.376 90.9972 20.934C90.7252 20.492 90.3512 20.118 89.8582 19.795C89.3652 19.489 88.7362 19.2 87.9542 18.928C86.9852 18.588 86.3902 18.299 86.1862 18.061C85.9652 17.823 85.8632 17.551 85.8632 17.228C85.8632 16.837 85.9822 16.531 86.2202 16.293C86.4582 16.072 86.7982 15.953 87.2062 15.953C87.5122 15.953 87.7672 16.038 88.0052 16.174C88.2432 16.327 88.3962 16.514 88.4982 16.752C88.5832 16.99 88.6342 17.228 88.6512 17.432H91.0142C90.9802 16.854 90.8782 16.344 90.6912 15.902C90.5042 15.46 90.2492 15.086 89.9262 14.78C89.6032 14.491 89.2122 14.27 88.7702 14.117C88.3112 13.964 87.8012 13.879 87.2572 13.879ZM97.0123 17.194C96.3663 17.194 95.7713 17.313 95.2273 17.517C94.6663 17.738 94.1903 18.044 93.7823 18.452C93.3743 18.86 93.0683 19.336 92.8473 19.88C92.6093 20.441 92.5073 21.036 92.5073 21.682C92.5073 22.328 92.6093 22.94 92.8473 23.501C93.0683 24.062 93.3573 24.521 93.7143 24.861C94.0543 25.201 94.4283 25.473 94.8193 25.677C95.2103 25.881 95.6013 26.017 95.9753 26.085C96.3493 26.153 96.7063 26.187 97.0123 26.187C97.6413 26.187 98.2363 26.085 98.7973 25.864C99.3583 25.643 99.8513 25.337 100.259 24.929C100.667 24.538 100.973 24.062 101.211 23.501C101.432 22.94 101.551 22.328 101.551 21.682C101.551 21.036 101.432 20.441 101.211 19.88C100.973 19.336 100.667 18.86 100.259 18.452C99.8513 18.044 99.3583 17.738 98.7973 17.517C98.2363 17.313 97.6413 17.194 97.0123 17.194ZM97.0293 19.2C97.5053 19.2 97.9303 19.336 98.3043 19.608C98.6613 19.88 98.9163 20.203 99.0523 20.594C99.1883 21.002 99.2563 21.359 99.2563 21.682C99.2563 22.022 99.1883 22.362 99.0693 22.719C98.9333 23.076 98.7633 23.365 98.5253 23.586C98.2873 23.807 98.0323 23.96 97.7773 24.045C97.5053 24.13 97.2673 24.164 97.0293 24.164C96.3493 24.164 95.8053 23.926 95.3973 23.433C94.9893 22.957 94.7853 22.362 94.7853 21.682C94.7853 21.359 94.8533 21.002 94.9893 20.594C95.1253 20.203 95.3803 19.88 95.7543 19.608C96.1113 19.336 96.5363 19.2 97.0293 19.2ZM110.266 17.381H108.073V22.005C108.073 22.668 107.92 23.178 107.648 23.518C107.359 23.875 107.002 24.045 106.594 24.045C106.186 24.045 105.88 23.926 105.676 23.654C105.472 23.382 105.37 22.991 105.37 22.481V17.381H103.16V22.974C103.16 23.977 103.381 24.759 103.857 25.337C104.333 25.915 105.013 26.187 105.931 26.187C106.458 26.187 106.917 26.068 107.308 25.83C107.699 25.592 108.005 25.269 108.209 24.878L108.379 26H110.266V17.381ZM116.996 17.194C116.452 17.194 115.976 17.33 115.568 17.568C115.16 17.806 114.871 18.129 114.667 18.503L114.497 17.381H112.61V26H114.803V21.444C114.803 20.781 114.939 20.271 115.228 19.897C115.517 19.523 115.891 19.336 116.333 19.336C116.724 19.336 117.03 19.472 117.234 19.744C117.438 20.016 117.557 20.39 117.557 20.883V26H119.784V20.407C119.784 19.421 119.529 18.639 119.053 18.061C118.577 17.483 117.88 17.194 116.996 17.194ZM130.183 13.59H127.973V18.316C127.735 17.993 127.378 17.721 126.919 17.517C126.46 17.313 125.95 17.194 125.389 17.194C124.811 17.194 124.284 17.313 123.791 17.534C123.298 17.772 122.873 18.078 122.516 18.486C122.142 18.894 121.87 19.387 121.666 19.931C121.462 20.492 121.377 21.07 121.377 21.682C121.377 22.294 121.462 22.889 121.666 23.433C121.87 23.994 122.142 24.487 122.516 24.895C122.873 25.303 123.298 25.626 123.791 25.847C124.284 26.085 124.811 26.187 125.372 26.187C126.579 26.187 127.497 25.745 128.109 24.844L128.279 26H130.183V13.59ZM125.831 19.268C126.239 19.268 126.613 19.404 126.987 19.642C127.361 19.88 127.616 20.186 127.769 20.543C127.922 20.917 128.007 21.274 128.007 21.631V21.733C128.007 22.107 127.922 22.464 127.786 22.838C127.633 23.212 127.378 23.518 127.004 23.756C126.63 23.994 126.239 24.113 125.831 24.113C125.389 24.113 124.981 23.994 124.607 23.722C124.233 23.467 123.995 23.144 123.859 22.77C123.723 22.396 123.655 22.039 123.655 21.682C123.655 21.24 123.74 20.849 123.91 20.509C124.08 20.169 124.318 19.863 124.658 19.625C124.998 19.387 125.389 19.268 125.831 19.268ZM134.708 13.59H132.515V26H134.402L134.572 24.844C135.184 25.745 136.085 26.187 137.309 26.187C137.853 26.187 138.38 26.085 138.89 25.847C139.383 25.626 139.808 25.303 140.182 24.895C140.539 24.487 140.811 23.994 141.015 23.433C141.219 22.889 141.321 22.294 141.321 21.682C141.321 21.07 141.219 20.492 141.015 19.931C140.811 19.387 140.539 18.894 140.182 18.486C139.808 18.078 139.383 17.772 138.89 17.534C138.38 17.313 137.853 17.194 137.309 17.194C136.731 17.194 136.204 17.313 135.745 17.517C135.286 17.721 134.946 17.993 134.708 18.316V13.59ZM136.85 19.268C137.292 19.268 137.666 19.387 138.006 19.625C138.346 19.863 138.601 20.169 138.771 20.509C138.941 20.849 139.026 21.24 139.026 21.682C139.026 22.124 138.941 22.532 138.771 22.872C138.601 23.229 138.363 23.518 138.023 23.756C137.683 23.994 137.292 24.113 136.85 24.113C136.442 24.113 136.051 23.994 135.677 23.756C135.303 23.518 135.031 23.212 134.895 22.838C134.742 22.464 134.674 22.107 134.674 21.733V21.631C134.674 21.274 134.742 20.917 134.895 20.543C135.048 20.186 135.303 19.88 135.677 19.642C136.051 19.404 136.442 19.268 136.85 19.268ZM145.284 17.381H143.074V26H145.284V17.381ZM144.179 13.539C143.805 13.539 143.482 13.675 143.227 13.93C142.955 14.185 142.836 14.491 142.836 14.848C142.836 15.222 142.955 15.528 143.227 15.783C143.482 16.038 143.805 16.157 144.179 16.157C144.536 16.157 144.842 16.038 145.114 15.783C145.386 15.528 145.522 15.222 145.522 14.848C145.522 14.491 145.386 14.185 145.114 13.93C144.842 13.675 144.536 13.539 144.179 13.539ZM150.376 15.29H148.149L148.132 17.381H146.721V19.387H148.132L148.098 23.399C148.081 24.351 148.302 25.048 148.778 25.473C149.254 25.915 149.968 26.119 150.937 26.119C151.532 26.119 151.991 26.068 152.331 25.932V23.994C152.059 24.079 151.753 24.113 151.43 24.113C151.022 24.113 150.733 24.028 150.563 23.824C150.393 23.637 150.308 23.314 150.308 22.872L150.342 19.387H152.331V17.381H150.359L150.376 15.29ZM157.752 17.194C157.123 17.194 156.545 17.313 156.018 17.534C155.474 17.755 155.015 18.061 154.641 18.469C154.267 18.877 153.978 19.37 153.774 19.914C153.57 20.475 153.468 21.07 153.468 21.699C153.468 22.362 153.57 22.974 153.774 23.535C153.978 24.096 154.267 24.572 154.641 24.963C155.015 25.371 155.474 25.677 156.018 25.881C156.545 26.085 157.123 26.187 157.752 26.187C158.381 26.187 158.959 26.102 159.469 25.898C159.979 25.694 160.421 25.388 160.812 24.963C161.186 24.538 161.492 23.977 161.696 23.297H159.554C159.435 23.62 159.214 23.875 158.908 24.045C158.602 24.215 158.228 24.3 157.82 24.3C157.378 24.3 157.004 24.198 156.664 23.994C156.324 23.79 156.103 23.518 155.967 23.212C155.831 22.906 155.746 22.634 155.746 22.362H161.747V21.682C161.747 21.138 161.679 20.645 161.577 20.169C161.458 19.693 161.288 19.285 161.084 18.928C160.863 18.588 160.608 18.282 160.319 18.044C160.013 17.806 159.656 17.602 159.231 17.432C158.806 17.279 158.313 17.194 157.752 17.194ZM157.701 19.03C158.228 19.03 158.653 19.2 158.993 19.54C159.333 19.88 159.503 20.305 159.52 20.832H155.763C155.797 20.458 155.916 20.118 156.12 19.812C156.324 19.523 156.579 19.319 156.885 19.2C157.174 19.098 157.446 19.03 157.701 19.03Z"
        fill="white"
      />
    </svg>
  );
};

const DialogBodyStyles = styled('div', {
  paddingBottom: '8px',
  '& p': {
    marginBottom: '$space$4',
  },
  '& li': {
    marginLeft: '$space$4',
  },
});

export const WelcomeDialog = observer(
  ({ defaultOpen }: { defaultOpen: boolean }) => {
    const model = AppFactory.soundbiteModel;

    return (
      <Dialog.PureContainer
        defaultOpen={defaultOpen}
        onDismiss={() => {
          model.dismissIntro();
        }}
        trigger={
          <IconButton
            icon={<HelpCircleIcon />}
            presentation="whiteTransparent"
            noFocus
            tabIndex={-1}
          />
        }
      >
        <Dialog.Heading>
          {__(
            'Welcome to Lupa Soundbites',
            'soundbites.welcomeToLupaSoundbites'
          )}
        </Dialog.Heading>
        <Dialog.Body>
          <DialogBodyStyles>
            <p>
              Listen to a short excerpt from a Lupa story and answer a question.
              When you're ready, check the answer.
            </p>
            <p>
              <em>For an extra listening challenge:</em>
            </p>
            <ul>
              <li>Hide some or all of the words</li>
              <li>Playback at 1.0x (native speed)</li>
            </ul>
          </DialogBodyStyles>
        </Dialog.Body>
        <Dialog.ButtonsContainer>
          <Dialog.Close asChild>
            <Dialog.Button
              presentation="teal"
              label={__("Let's go", 'common.letsGo')}
              fullWidth
            />
          </Dialog.Close>
        </Dialog.ButtonsContainer>
      </Dialog.PureContainer>
    );
  }
);

const Back = () => {
  const navigate = useNavigate();

  // return <Logo />;

  return (
    <HStack>
      <IconButton
        icon={<ArrowLeftSmallIcon />}
        presentation="whiteTransparent"
        noFocus
        tabIndex={-1}
        onClick={() => {
          if (ReturnNavState.canPopFromStudy()) {
            navigate(-1);
          } else {
            navigate(homePath());
          }
        }}
      />
      <HSpacer size={2} />
      <Logo />
    </HStack>
  );
};

export const SbHeader = observer(({ story }: { story: Story }) => {
  const model = usePlayerModel();
  const ref = React.useRef<HTMLDivElement>(null);
  const autoShowWelcomeDialog = model.onloadModalNeeded;

  useReaction(
    () => model.isPlaying,
    () => {
      if (model.isPlaying) {
        ref.current?.classList.add('playing');
      } else {
        ref.current?.classList.remove('playing');
      }
    }
  );
  return (
    <Wrapper css={{ $$backgroundColor: story.themeColor }} ref={ref}>
      <span className="left">
        {/* <Logo /> */}
        <Back />
      </span>
      {/* <span className="date">August 27, 2022</span> */}
      <span className="right">
        <WelcomeDialog defaultOpen={autoShowWelcomeDialog} />
      </span>
    </Wrapper>
  );
});
